const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuid(value: string | null | undefined): value is string {
  return Boolean(value && UUID_PATTERN.test(value));
}

const INVALID_STORE_IDS = new Set(["all", "null", "undefined"]);

function concreteStoreId(value: string | null | undefined): string | null {
  const normalized = value?.trim();
  if (!normalized || INVALID_STORE_IDS.has(normalized.toLowerCase())) {
    return null;
  }
  return normalized;
}

/**
 * Resolve the database store selected by the shell.
 *
 * Store-scoped APIs use UUID foreign keys. Persisted aliases from older
 * clients (for example `main-store`) must therefore never be sent as a
 * storeId. Prefer the selected UUID, then the authenticated store, and finally
 * the sole UUID-backed store available to the user.
 */
export function resolveValidStoreId(
  selectedStoreId: string | null | undefined,
  userStoreId?: string | null,
  availableStores: ReadonlyArray<{ id: string }> = [],
): string | null {
  const selected = concreteStoreId(selectedStoreId);
  if (isUuid(selected)) return selected;
  const userStore = concreteStoreId(userStoreId);
  if (isUuid(userStore)) return userStore;

  const validStores = availableStores
    .map((store) => concreteStoreId(store.id))
    .filter((storeId): storeId is string => isUuid(storeId));
  return validStores.length === 1 ? validStores[0] : null;
}
